import { useMutation, useQueryClient, UseMutationResult } from '@tanstack/react-query';
import GenericDataProvider from '../../common/api/GenericDataProvider';
import { AdminModelMap, AdminObjectName } from '../common/DataColumnConfig';

export interface CustomQueryConfig {
  method: string;
  url: string;
  data?: unknown;
}

export interface UseMutateDataResult<T = unknown> {
  createMutation: UseMutationResult<unknown, unknown, Partial<T>>;
  updateMutation: UseMutationResult<unknown, unknown, Partial<T> & { id: string | number }>;
  deleteMutation: UseMutationResult<unknown, unknown, string | number>;
  customQueryMutation: UseMutationResult<unknown, unknown, CustomQueryConfig>;
}

/**
 * Hook for managing data mutations (Create, Update, Delete, Custom Query).
 * Automatically invalidates relevant queries on success.
 */
const useMutateData = <K extends AdminObjectName>(objectName: K): UseMutateDataResult<AdminModelMap[K]> => {
  const dataProvider = new GenericDataProvider();
  const queryClient = useQueryClient();

  const invalidateQueries = () => {
    // Invalidate standard admin list queries (e.g., ['courses'])
    queryClient.invalidateQueries({ queryKey: [objectName] });

    // Invalidate Orval-generated detail queries.
    // Orval uses keys like ['/courses/123']. We use a predicate to match
    // any query key whose first element starts with the object path.
    queryClient.invalidateQueries({
      predicate: (query) => {
        const firstKey = query.queryKey[0];
        if (typeof firstKey !== 'string') return false;
        return firstKey.startsWith(`/${objectName}`);
      },
    });
  };

  const createMutation = useMutation({
    mutationFn: (newData: unknown) => dataProvider.create(objectName, newData),
    onSuccess: invalidateQueries,
  });

  const updateMutation = useMutation({
    mutationFn: (newData: unknown) => dataProvider.update(objectName, (newData as { id?: string | number })?.id || '', newData),
    onSuccess: invalidateQueries,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string | number) => dataProvider.delete(objectName, id),
    onSuccess: invalidateQueries,
  });

  const customQueryMutation = useMutation({
    mutationFn: (config: CustomQueryConfig) => dataProvider.customQuery(config.method, config.url, config.data),
    onSuccess: invalidateQueries,
  });

  return {
    createMutation: createMutation as unknown as UseMutationResult<unknown, unknown, Partial<AdminModelMap[K]>>,
    updateMutation: updateMutation as unknown as UseMutationResult<unknown, unknown, Partial<AdminModelMap[K]> & { id: string | number }>,
    deleteMutation: deleteMutation as unknown as UseMutationResult<unknown, unknown, string | number>,
    customQueryMutation: customQueryMutation as unknown as UseMutationResult<unknown, unknown, CustomQueryConfig>,
  };
};

export default useMutateData;
